import './VideoStyles.css';

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { googlePalService } from 'react-native-google-pal';
import { useRecoilValue } from 'recoil';
import { SECOND } from 'time-constants';

import { apiConfig } from '@constants';
import { userSettingsState } from '@store';

import { sendBookmark } from './bookmarking/sendBookmark';
import { VideoControls } from './controls/Controls';
import { ControlsContainer } from './controls/ControlsContainer';
import { ReactHLSPlayer } from './ReactHLSPlayer';
import { VideoLoadingIndicator } from './VideoLoadingIndicator';

import { isX1 } from '../../helpers/Platform';
import { CaptionsDisplay } from '../CaptionsDisplay/CaptionsDisplay';
import { segmentEventTypes } from '../segment/segmentEventTypes';
import { segmentTrackEvent } from '../segment/SegmentTrackEvent';

export const Video = forwardRef(
  (
    {
      source,
      stopped = false,
      data,
      ads,
      isFullscreen,
      nextFocusUp,
      height,
      width,
      translation,
      vodPlaylistReference,
      onVideoEnd: onVideoEndProp = () => {},
      onVideoProgress = () => {},
    },
    reference,
  ) => {
    const playerReference = useRef();
    const playerError = useRef(null);

    const { t } = useTranslation();
    const errorTPath = 'errors.';

    const userSettings = useRecoilValue(userSettingsState);
    const ccEnabled = userSettings.closedCaptionsEnabled;

    const lastBookmark = useRef(0);
    const lastQuartile = useRef(0);
    const [lastHeartbeat, setLastHeartbeat] = useState(null);
    const [caption, setCaption] = useState('');

    const playPoint = useRef(null);
    const [currentSource, setNewCurrentSource] = useState('');
    const timer = useRef();

    const isLoadingPlay = useRef(false);

    const [playerReady, setPlayerReady] = useState(Boolean(playerReference.current));
    const isPlaybackStopped = !playerReady || stopped || !document.contains(playerReference.current);

    useEffect(() => {
      if (playerReference.current) {
        setPlayerReady(true);
      }
    }, [playerReference]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setCurrentSource = newSource => {
      console.log(`video content setting source: ${newSource}. Old source: ${currentSource}`);
      if (currentSource !== '' && playPoint.current !== null) {
        const currentTime = playerReference.current?.currentTime ?? 0;
        const duration = currentTime - playPoint.current;

        console.log(`CONTENT STOPPED. PLAY TIME: ${duration}`);
        playPoint.current = null;
        if (isX1) {
          window.$badger.appActionMetricsHandler('CONTENT_STATE_CHANGE', {
            contentState: 'STOPPED',
            duration,
          });
        }
      }

      if (newSource) {
        console.log('CONTENT STARTED');
        setLastHeartbeat(null);
        segmentTrackEvent(segmentEventTypes.VIDEO_PLAYBACK_STARTED, {
          ...userSettings,
          ...data,
        });
        if (isX1) {
          window.$badger.appActionMetricsHandler('CONTENT_STATE_CHANGE', {
            contentState: 'STARTED',
          });
        }
      }

      resetPlayerState();
      setNewCurrentSource(newSource ?? '');
      setCaption('');
    };

    if (isPlaybackStopped && playPoint.current !== null) {
      const currentTime = playerReference.current?.currentTime ?? 0;
      const duration = currentTime - playPoint.current;

      console.log(`CONTENT STOPPED. PLAY TIME: ${duration}`);
      if (duration > 0) {
        setLastHeartbeat(null);
        segmentTrackEvent(segmentEventTypes.VIDEO_PLAYBACK_COMPLETED, {
          ...userSettings,
          ...data,
        });
        if (isX1) {
          window.$badger.appActionMetricsHandler('CONTENT_STATE_CHANGE', {
            contentState: 'STOPPED',
            duration,
          });
        }
      }
      playPoint.current = null;
      setNewCurrentSource('');
    }

    const [playerData, setPlayerData] = useState({
      paused: false,
      playing: false,
      seeking: false,
      loading: false,
      error: false,
      progress: 0,
      duration: 1,
    });

    const setStateToPlaying = () =>
      setPlayerData({
        ...playerData,
        paused: false,
        playing: true,
        seeking: false,
        loading: false,
        error: false,
      });

    const resetPlayerState = () =>
      setPlayerData({
        ...playerData,
        paused: false,
        playing: false,
        seeking: false,
        loading: false,
        error: false,
      });

    const playVideo = (seekingOverride = false) => {
      if (!playerReference.current?.ended && !isPlaybackStopped && !playerData.playing) {
        if (!seekingOverride && playerData.seeking !== false) {
          setPlayerData({ ...playerData, seeking: false });
          seekTo(playerData.seeking);
        } else if (currentSource && !isLoadingPlay.current) {
          isLoadingPlay.current = true;
          playerReference.current
            .play()
            .then(() => {
              isLoadingPlay.current = false;
              googlePalService.sendPlaybackStart();
              setStateToPlaying();
            })
            .catch(error => {
              console.warn('Error playing video');
              console.warn(error);
              isLoadingPlay.current = false;
              playerError.current = error;
              playerError.current.message = t(`${errorTPath}genericError`);
              if (isX1) {
                window.$badger.errorMetricsHandler('Failed To start playback', true, {
                  isLive: data.isLive,
                  url: currentSource,
                });
              }
              setStateError();
            });
        }
      }
    };

    const pauseVideo = () => {
      playerReference.current.pause();
      if (!playerData.paused) {
        googlePalService.sendPlaybackEnd();
        setPlayerData({ ...playerData, paused: true, playing: false });
      }
    };

    const seekTo = position => {
      const clamped = Math.min(Math.max(position, 0), data.duration);

      playerReference.current.currentTime = clamped;
      if (playPoint.current !== null && clamped < playPoint.current) {
        playPoint.current = clamped;
      }
      playVideo(true);
    };

    const setSeeking = position => {
      const clamped = Math.min(Math.max(position, 0), data.duration);

      setPlayerData({
        ...playerData,
        seeking: clamped,
        loading: false,
        error: false,
      });
    };

    // Video heartbeat
    useEffect(() => {
      if (!isPlaybackStopped && currentSource) {
        if (!lastHeartbeat) {
          setLastHeartbeat(Date.now());
          return;
        }
        const nextHeartbeat = lastHeartbeat + 60 * SECOND;
        const now = Date.now();

        let delay = 0;

        if (now < nextHeartbeat) {
          delay = nextHeartbeat - now;
        }

        timer.current = setTimeout(() => {
          segmentTrackEvent(segmentEventTypes.VIDEO_HEARTBEAT, {
            ...userSettings,
            ...data,
          });

          setLastHeartbeat(Date.now());
        }, delay);
      }
      return () => {
        clearTimeout(timer.current);
      };
    }, [isPlaybackStopped, currentSource, userSettings, data, lastHeartbeat]);

    // Monitor errors and force a retry by updating the source
    useEffect(() => {
      const retry = retryParameter => {
        if (source && source !== '') {
          // Append a new param to force an update. Will destroy hls session and restart
          const newSource = source?.includes('?')
            ? `${source}&retry=${retryParameter}`
            : `${source}?retry=${retryParameter}`;

          setCurrentSource(newSource);
        }
      };

      let timeout;

      // On player error, force a retry and reset the player state
      if (playerData.error && !playerData.loading && !isPlaybackStopped) {
        if (playerError.current?.error?.type === 'networkError' && !playerError.current?.error?.fatal) {
          return;
        }

        timeout = setTimeout(() => {
          retry(Date.now());
          setPlayerData({
            ...playerData,
            loading: true,
            playing: false,
          });
        }, 5 * SECOND);
      }
      return () => {
        clearTimeout(timeout);
      };
    }, [playerData, source, isPlaybackStopped, currentSource, setCurrentSource]);

    // Play/pause the video
    useEffect(() => {
      if (!isPlaybackStopped && currentSource !== '' && playerReference.current.readyState >= 1) {
        if (playerData.paused) {
          pauseVideo();
        } else {
          playVideo();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerReference, currentSource]);

    // Checks on every render if we need to resume playback
    useEffect(() => {
      if (
        !isPlaybackStopped &&
        playerReference.current.paused &&
        !playerData.paused &&
        lastQuartile.current !== 4 &&
        !isLoadingPlay.current
      ) {
        isLoadingPlay.current = true;
        playerReference.current
          .play()
          .then(() => {
            isLoadingPlay.current = false;
            setStateToPlaying();
          })
          .catch(() => {
            isLoadingPlay.current = false;
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerReference, currentSource, isLoadingPlay, playerData]);

    if (
      !isPlaybackStopped &&
      playerReference.current &&
      playerReference.current.paused &&
      !playerData.paused &&
      lastQuartile.current !== 4 &&
      !isLoadingPlay.current
    ) {
      isLoadingPlay.current = true;
      playerReference.current
        .play()
        .then(() => {
          isLoadingPlay.current = false;
          setStateToPlaying();
        })
        .catch(() => {
          isLoadingPlay.current = false;
        });
    }

    // Only set a new source if the base URL changes. This prevents the player
    // from reloading if the live feed updates with only new ads params.
    useEffect(() => {
      if (!isPlaybackStopped) {
        if (currentSource) {
          const lastSource = currentSource?.split('?')[0];
          const newSource = source?.split('?')[0];

          if (lastSource === newSource) {
            if (lastSource !== newSource) {
              playerReference.current.src = '';
              setCurrentSource(source);
              playPoint.current = null;
            }
          } else {
            playerReference.current.src = '';
            setCurrentSource(source);
            playPoint.current = null;
          }
        } else {
          playerReference.current.src = '';
          setCurrentSource(source);
          playPoint.current = null;
        }
      }
    }, [source, currentSource, setCurrentSource, isPlaybackStopped]);

    const onVideoStart = useCallback(() => {
      setStateToPlaying();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerData]);

    // Update player progress
    const onTimeUpdate = useCallback(() => {
      const currentTime = playerReference.current?.currentTime ?? 0;
      const duration = data.duration;

      if (playPoint.current === null) {
        playPoint.current = currentTime;
      }

      onVideoProgress && onVideoProgress(currentTime, duration);
      if (data.isLive) {
        if (playerData.error) {
          setPlayerData({
            ...playerData,
            error: false,
          });
        }
      } else {
        const progress = currentTime / duration;
        const quartile = Math.floor(progress * 4);

        if (quartile !== lastQuartile.current) {
          lastQuartile.current = quartile;
          if (quartile > 0) {
            console.log(`video quartile reached: ${quartile}`);
            segmentTrackEvent(segmentEventTypes.VIDEO_QUARTILE_VOD, {
              ...userSettings,
              ...data,
            });
          }
        }
        setPlayerData({
          ...playerData,
          duration,
          error: false,
          progress,
        });
      }

      if (data.needsBookmarking && currentTime >= 5) {
        const sinceLastBookmark = Math.abs(currentTime - lastBookmark.current);

        if (sinceLastBookmark >= 15) {
          sendBookmark(currentTime, data.videoID, data.seasonID);
          lastBookmark.current = currentTime;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiConfig.DSP_SEND_BOOKMARK_URL, data, playerData, playerReference, lastQuartile]);

    const setStateError = useCallback(() => {
      console.warn('video playback error');
      setPlayerData({
        ...playerData,
        playing: false,
        error: true,
        loading: false,
      });
    }, [playerData]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onVideoBuffer = useCallback(() => {
      console.log('Video buffering...');
      setPlayerData({
        ...playerData,
        playing: false,
        loading: true,
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onComplete = useCallback(() => {
      console.log('Video complete');
      if (lastQuartile.current !== 4) {
        lastQuartile.current = 4;
        segmentTrackEvent(segmentEventTypes.VIDEO_QUARTILE_VOD, {
          ...userSettings,
          ...data,
        });
      }
      segmentTrackEvent(segmentEventTypes.VIDEO_PLAYBACK_COMPLETED, {
        ...userSettings,
        ...data,
      });
      setLastHeartbeat(null);
      onVideoEndProp && onVideoEndProp();
    });

    useEffect(() => {
      const onError = error => {
        console.warn(error);
        segmentTrackEvent(segmentEventTypes.VIDEO_ERROR_LOG, userSettings, {
          videoErrorData: {
            source,
            exception: error.details,
            responseCode: error.type,
            response: error.details,
          },
        });
        playerError.current = error;
        if (isX1) {
          window.$badger.errorMetricsHandler('Failed To start playback', true, {
            isLive: data.isLive,
            url: currentSource,
          });
        }
        setStateError();
      };

      playerReference.current.addEventListener('play', onVideoStart);
      playerReference.current.addEventListener('playing', onVideoStart);
      playerReference.current.addEventListener('waiting', onVideoBuffer);
      playerReference.current.addEventListener('timeupdate', onTimeUpdate);
      playerReference.current.addEventListener('error', onError);
      playerReference.current.addEventListener('ended', onComplete);
      return () => {
        playerReference.current?.removeEventListener('play', onVideoStart);
        playerReference.current?.removeEventListener('playing', onVideoStart);
        playerReference.current?.removeEventListener('waiting', onVideoBuffer);
        playerReference.current?.removeEventListener('timeupdate', onTimeUpdate);
        playerReference.current?.removeEventListener('error', onError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        playerReference.current?.removeEventListener('ended', onComplete);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerReference, onTimeUpdate, onVideoStart, onVideoBuffer, setStateError]);

    useEffect(() => {
      const onCueChange = event => {
        if (!event.target.activeCues) {
          return;
        }
        if (event.target.activeCues.length > 0 && event.target.activeCues[0]) {
          const cues = [];

          for (let index = 0; index < event.target.activeCues.length; index++) {
            if (event.target.activeCues[index]) {
              const cue = event.target.activeCues[index];

              cues.push({ text: cue.text ?? '', line: cue.line ?? index });
            }
          }
          cues.sort((a, b) => a.line - b.line);
          const texts = cues.map(cue => cue.text);

          setCaption(texts.join('\n'));
        } else {
          setCaption('');
        }
      };

      const onTracksChange = () => {
        let track = playerReference?.current?.textTracks[0];

        for (const textTrack of playerReference?.current?.textTracks ?? []) {
          textTrack.mode = 'disabled';
          textTrack.removeEventListener('cuechange', onCueChange);
          if (textTrack.cues !== null && textTrack.cues.length > 0) {
            track = textTrack;
          }
          if (textTrack.label === 'caption_1') {
            track = textTrack;
          }
        }
        if (track && ccEnabled) {
          track.mode = 'hidden';
        }

        track?.addEventListener('cuechange', onCueChange);
      };

      const onTrackRemoved = event => {
        if (event.track) {
          event.track?.removeEventListener('cuechange', onCueChange);
        }
      };

      const onTrackChanged = () => {
        for (const track of playerReference?.current?.textTracks ?? []) {
          if (track.mode === 'showing') {
            track.mode = 'hidden';
            return;
          }
        }
      };

      // Hidden hls player subtitles render to use our custom subtitle render
      let track = playerReference?.current?.textTracks[0];

      for (const textTrack of playerReference?.current?.textTracks ?? []) {
        textTrack.mode = 'disabled';
        if (textTrack.cues !== null && textTrack.cues.length > 0) {
          track = textTrack;
        }
        if (textTrack.label === 'caption_1') {
          track = textTrack;
        }
      }
      if (track && ccEnabled) {
        track.mode = 'hidden';
      }

      if (ccEnabled) {
        playerReference.current?.textTracks?.addEventListener('addtrack', onTracksChange);
        playerReference?.current?.textTracks?.addEventListener('removetrack', onTrackRemoved);
        playerReference?.current?.textTracks?.addEventListener('change', onTrackChanged);

        track?.addEventListener('cuechange', onCueChange);
      }

      return () => {
        playerReference.current?.textTracks?.removeEventListener('addtrack', onTracksChange);
        playerReference?.current?.textTracks?.removeEventListener('removetrack', onTrackRemoved);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        playerReference?.current?.textTracks?.removeEventListener('change', onTrackChanged);
        track?.removeEventListener('cuechange', onCueChange);
      };
    }, [playerReference, currentSource, playerData?.paused, playerData?.playing, ccEnabled]);

    const reload = () => {
      playerReference.current?.pause();
      playerReference.current.currentTime = 0;
      playVideo();
    };

    useImperativeHandle(reference, () => ({
      reloadVideo: () => {
        reload();
      },
    }));

    const showLoadingIndicator = playerData.loading || playerData.error;
    const hideDisplay = { display: 'none' };

    return (
      <>
        {isFullscreen && (
          <ControlsContainer
            controlsOnRef={vodPlaylistReference}
            data={{ ...data, ...playerData }}
            isLive={data.isLive}
            nextFocusUp={nextFocusUp}
            preventButtons={data.disableInputs ? ['all'] : []}
            onControl={(control, value) => {
              switch (control) {
                case VideoControls.PAUSE:
                  pauseVideo();
                  break;
                case VideoControls.PLAY:
                  playVideo();
                  break;
                case VideoControls.SEEK:
                  seekTo(value);
                  break;
                case VideoControls.SEEKING:
                  setSeeking(value);
                  break;
                case VideoControls.TOGGLE_PAUSE:
                  if (playerData.paused) {
                    playVideo();
                  } else {
                    pauseVideo();
                  }
              }
            }}
          />
        )}
        {showLoadingIndicator && (
          <VideoLoadingIndicator
            hasError={playerData.error}
            height={height}
            message={playerData.error ? playerError.current?.message : null}
            translation={translation}
            width={width}
          />
        )}
        <View style={[showLoadingIndicator ? hideDisplay : null]}>
          <CaptionsDisplay caption={caption} enabled={ccEnabled} width={width} />
          <ReactHLSPlayer
            playsInline
            autoPlay
            height={'100%'}
            hlsConfig={{
              lowLatencyMode: true,
              backBufferLength: 30,
              maxBufferSize: 60 * 1024 * 1024,
              //debug: true,
              enableWebVTT: true,
            }}
            loop={false}
            ref={playerReference}
            src={currentSource}
            startPosition={data?.startPosition}
            stopped={isPlaybackStopped}
            width={'100%'}
          />
        </View>
      </>
    );
  },
);
